import React, { useState } from 'react';
import AdminEvents from './AdminEvents';
import AdminGames from './AdminGames';
import AdminWebhooks from './AdminWebhooks';
import { getAdminToken } from './AdminGate';
import { useToast } from './Toast';

/**
 * AdminPanel — Tabbed admin interface for managing the site.
 * Tabs: Events | Games Catalog | Announcement | Webhooks
 * Protected by AdminGate in App.jsx.
 */
export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('events');

  const tabs = [
    { id: 'events', label: 'Events', icon: '📅' },
    { id: 'games', label: 'Game Catalog', icon: '🎮' },
    { id: 'announce', label: 'Announce', icon: '📢' },
    { id: 'webhooks', label: 'Webhooks', icon: '📡' },
  ];

  return (
    <section className="px-4 md:px-6 py-8 md:py-12">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="mb-6 p-4 rounded-lg bg-amber-500/5 border border-amber-500/15">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-amber-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-sm font-bold text-amber-400/80 font-mono">Admin Panel</h2>
          </div>
          <p className="text-xs text-amber-400/40 font-mono">Manage events, games, announcements, and webhooks</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-mono transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white/[0.08] text-white border border-white/[0.1] shadow-sm'
                  : 'text-white/40 hover:text-white/60 border border-transparent'
              }`}
            >
              <span className="text-base leading-none">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'events' && <AdminEvents />}
        {activeTab === 'games' && <AdminGames />}
        {activeTab === 'announce' && <AdminAnnouncement />}
        {activeTab === 'webhooks' && <AdminWebhooks />}
      </div>
    </section>
  );
}

/**
 * AdminAnnouncement — Manage site-wide announcement banner.
 */
function AdminAnnouncement() {
  const toast = useToast();
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [link, setLink] = useState('');
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch current announcement
  React.useEffect(() => {
    fetch('/api/announcement')
      .then(r => r.json())
      .then(data => {
        if (data && data.message) {
          setCurrent(data);
          setMessage(data.message);
          setType(data.type || 'info');
          setLink(data.link || '');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const saveAnnouncement = async () => {
    if (!message.trim()) {
      toast.warn('Message cannot be empty');
      return;
    }
    try {
      const res = await fetch('/api/announcement', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({ message, type, link: link || null }),
      });
      const data = await res.json();
      if (data.success) {
        setCurrent(data.announcement);
        toast.success('📢 Announcement published!');
      } else {
        toast.error(data.error || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save announcement');
    }
  };

  const clearAnnouncement = async () => {
    try {
      const res = await fetch('/api/announcement', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setCurrent(null);
        setMessage('');
        setLink('');
        toast.success('Announcement cleared');
      }
    } catch {
      toast.error('Failed to clear announcement');
    }
  };

  const typeOptions = [
    { value: 'info', label: 'ℹ️ Info', color: 'text-blue-400' },
    { value: 'warn', label: '⚠️ Warning', color: 'text-amber-400' },
    { value: 'success', label: '🎉 Success', color: 'text-emerald-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        <h3 className="text-xs font-mono text-white/40 uppercase tracking-wider mb-3">Current Banner</h3>
        {loading ? (
          <div className="skeleton h-8 w-full" />
        ) : current ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-white/70 font-mono flex-1 truncate">"{current.message}"</p>
            <span className="text-[10px] font-mono text-white/25 flex-shrink-0">
              {current.type} · {new Date(current.updatedAt).toLocaleDateString()}
            </span>
          </div>
        ) : (
          <p className="text-xs text-white/25 font-mono">No active announcement</p>
        )}
      </div>

      {/* Editor */}
      <div className="space-y-4">
        <div>
          <label className="block text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1.5">Message *</label>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g., Community Gaming Night — this Saturday at 8PM PDT!"
            className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/90 text-sm font-mono placeholder:text-white/20 focus:outline-none focus:border-emerald-500/30 transition-colors"
            maxLength={200}
          />
          <span className="text-[10px] font-mono text-white/20 mt-1 block text-right">{message.length}/200</span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1.5">Type</label>
            <div className="flex gap-2">
              {typeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setType(opt.value)}
                  className={`flex-1 px-2.5 py-2 rounded-md text-xs font-mono text-center transition-all cursor-pointer ${
                    type === opt.value
                      ? `${opt.color} bg-white/[0.08] border border-white/[0.12]`
                      : 'text-white/30 bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1.5">Link (optional)</label>
            <input
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://discord.gg/..."
              className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/90 text-sm font-mono placeholder:text-white/20 focus:outline-none focus:border-emerald-500/30 transition-colors"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={saveAnnouncement}
            className="px-5 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-xs font-mono font-bold hover:bg-emerald-500/25 transition-colors cursor-pointer"
          >
            {current ? '✏️ Update Banner' : '📢 Publish Banner'}
          </button>
          {current && (
            <button
              onClick={clearAnnouncement}
              className="px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400/70 text-xs font-mono hover:bg-red-500/20 transition-colors cursor-pointer"
            >
              🗑️ Clear Banner
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {message.trim() && (
        <div>
          <h3 className="text-[10px] font-mono text-white/30 uppercase tracking-wider mb-2">Preview</h3>
          <div
            className="p-3 rounded-lg flex items-center gap-3"
            style={{
              background: type === 'warn'
                ? 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))'
                : type === 'success'
                ? 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.03))'
                : 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(59,130,246,0.03))',
              border: `1px solid ${
                type === 'warn' ? 'rgba(245,158,11,0.15)' : type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(59,130,246,0.15)'
              }`,
            }}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              type === 'warn' ? 'bg-amber-400' : type === 'success' ? 'bg-emerald-400' : 'bg-blue-400'
            }`} />
            <p className={`text-xs font-mono ${
              type === 'warn' ? 'text-amber-300/90' : type === 'success' ? 'text-emerald-300/90' : 'text-blue-300/90'
            }`}>
              {message}
              {link && <span className="ml-2 underline opacity-60">Learn more →</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
